import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../../../../user-management/auth-management/authentication/services/authentication.service';
import { Token } from '../../../../user-management/auth-management/authentication/models/token.model';
import { NetworkStatusService } from '../../../../../trash/shared/services/network-status/network-status.service';

// Centralizes bearer header cloning so normal and post-refresh requests stay identical.
function addAccessToken(req: HttpRequest<unknown>, token: Token): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token.accessToken}`
    }
  });
}

export const AccessTokenHttpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const authService = inject(AuthenticationService);
  const networkStatusService = inject(NetworkStatusService);

  // Check if offline before proceeding
  if (!networkStatusService.isOnline()) {
    // Show offline error and block the request
    networkStatusService.showOfflineRequestError();
    return EMPTY; // Break the flow without making a request or throwing an error
  }

  const excludedRoutes: string[] = ['refresh'];
  const isExcluded = excludedRoutes.some((route) => req.url.includes(route));

  // If the route is excluded, proceed without attaching the token
  if (isExcluded) {
    return next(req);
  }

  // Retrieve the token
  const token: Token | undefined = authService.ensureAuthenticated();

  if (!token) {
    return next(req);
  }

  if (token.isAccessTokenExpired()) {
    return authService.refreshAccessToken(token).pipe(
      switchMap((refreshedToken) => next(addAccessToken(req, refreshedToken))),
      catchError(() => {
        authService.logout();
        return EMPTY;
      })
    );
  }

  return next(addAccessToken(req, token));
};
